import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon } from 'lucide-react';
import { useEffect } from 'react';
import { z } from 'zod';

import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { CloseablePageLayout } from '@/components/CloseablePageLayout';
import { AzureDevOpsConfiguration } from '@/components/GitIntegration/AzureDevOpsConfiguration';
import { BitbucketConfiguration } from '@/components/GitIntegration/BitbucketConfiguration';
import { BitbucketDataCenterConfiguration } from '@/components/GitIntegration/BitbucketDataCenterConfiguration';
import { GerritConfiguration } from '@/components/GitIntegration/GerritConfiguration';
import { GitHubConfiguration } from '@/components/GitIntegration/GitHubConfiguration';
import { GitLabConfiguration } from '@/components/GitIntegration/GitLabConfiguration';
import { JiraCloudConfiguration } from '@/components/TicketAnalysis/JiraCloudConfiguration';
import { Button } from '@/components/ui/button';
import { GitProviderProvider } from '@/contexts/GitProviderContext';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

// Integration type constants
const INTEGRATION_TYPES = {
  'azure-devops': 'azure-devops',
  bitbucket: 'bitbucket',
  'bitbucket-data-center': 'bitbucket-data-center',
  gerrit: 'gerrit',
  github: 'github',
  gitlab: 'gitlab',
  'jira-cloud': 'jira-cloud',
} as const;

type IntegrationType = (typeof INTEGRATION_TYPES)[keyof typeof INTEGRATION_TYPES];

const integrationSearchSchema = z.object({
  installId: searchParam().optional(),
  providerId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/settings/integrations/$integrationType')({
  component: SecuredIntegrationTypePage,
  validateSearch: integrationSearchSchema,
  staticData: {
    hideLeftSidebar: true,
    hideLeftMainHeader: true,
  },
});

function SecuredIntegrationTypePage() {
  return (
    <RequireEntitlements admins={['isSuperAdmin']}>
      <IntegrationTypePage />
    </RequireEntitlements>
  );
}

function IntegrationTypePage() {
  const { integrationType } = Route.useParams();
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Configure ${getIntegrationTitle(integrationType)}`;
  }, [integrationType, organizationDisplayName]);

  // Validate integration type
  if (!Object.values(INTEGRATION_TYPES).includes(integrationType as IntegrationType)) {
    return (
      <CloseablePageLayout
        closeTo="/$organizationName/settings/integrations"
        closeParams={{ organizationName: routeOrganizationName }}
        header="Invalid Integration"
        subHeader={`Unknown integration type: ${integrationType}`}
      >
        <Button
          variant="outline"
          onClick={() =>
            navigate({
              to: '/$organizationName/settings/integrations',
              params: { organizationName: routeOrganizationName },
            })
          }
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Integrations
        </Button>
      </CloseablePageLayout>
    );
  }

  return (
    <CloseablePageLayout
      closeTo="/$organizationName/settings/integrations"
      closeParams={{ organizationName: routeOrganizationName }}
      header={getIntegrationTitle(integrationType)}
      subHeader={getIntegrationSubtitle(integrationType)}
    >
      {integrationType === 'jira-cloud' && <JiraCloudConfiguration />}
      {integrationType !== 'jira-cloud' && (
        <GitProviderProvider>
          {integrationType === 'gitlab' && <GitLabConfiguration />}
          {integrationType === 'bitbucket' && <BitbucketConfiguration />}
          {integrationType === 'bitbucket-data-center' && <BitbucketDataCenterConfiguration />}
          {integrationType === 'gerrit' && <GerritConfiguration />}
          {integrationType === 'azure-devops' && <AzureDevOpsConfiguration />}
          {integrationType === 'github' && <GitHubConfiguration />}
        </GitProviderProvider>
      )}
    </CloseablePageLayout>
  );
}

function getIntegrationTitle(type: string): string {
  switch (type) {
    case 'azure-devops':
      return 'Azure DevOps Cloud';
    case 'bitbucket':
      return 'Bitbucket Cloud';
    case 'bitbucket-data-center':
      return 'Bitbucket Data Center';
    case 'gerrit':
      return 'Gerrit';
    case 'github':
      return 'GitHub';
    case 'gitlab':
      return 'GitLab';
    case 'jira-cloud':
      return 'Jira Cloud';
    default:
      return 'Integration';
  }
}

function getIntegrationSubtitle(type: string): string {
  switch (type) {
    case 'azure-devops':
      return 'Follow these steps to connect your Azure DevOps Cloud account.';
    case 'bitbucket':
      return 'Follow these steps to connect your Bitbucket account.';
    case 'bitbucket-data-center':
      return 'Follow these steps to connect your Bitbucket Data Center instance.';
    case 'gerrit':
      return 'Follow these steps to connect your Gerrit instance.';
    case 'github':
      return 'Configure your GitHub integration.';
    case 'gitlab':
      return 'Follow these steps to connect your GitLab account.';
    case 'jira-cloud':
      return 'Connect your Jira Cloud site to enable automatic ticket analysis.';
    default:
      return '';
  }
}
